import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import { Redirect } from 'react-router'

import AccessibleForm from '../../../../components/accessibility/AccessibleForm/AccessibleForm'
import ActionButton from '../../../../components/ui/ActionButton/ActionButton'
import Layout from '../../../../components/ui/Layout/Layout'
import AddCardForm from '../../../../components/home/AddCardForm/AddCardForm'
import AddCardSuccessModal from '../../../../components/home/AddCardSuccessModal/AddCardSuccessModal'
import CardSlider from '../../../../components/home/CardSlider/CardSlider'
import ZingIcon from '../../../../assets/img/zing-icon.svg'

import CardList from '../../../../components/home/CardList/CardList'
import SkipAddCardForm from '../SkipAddCardForm/SkipAddCardForm'

import {
  BANNER_ADS,
  BANNER_ADS_EMPTY,
} from '../../../../services/data/errorNames.json'
import {
  setGACustomEvent,
  setGACustomPageView,
} from '../../../../services/analytics/google-analytics'
import {
  LANDING,
  ADD_CARD,
} from '../../../../services/data/registrationSteps.json'
import media from '../../../../services/generateMediaQueries'

import truncateString from '../../../../services/data/truncateString'
import { postDevices } from '../../../../services/api/requests/Device'

const Title = styled.h1`
  font-size: 1.5rem;
  text-align: center;
  margin-top: 0;
  font-weight: var(--font-weight-boldest);
  color: var(--color-secondary);
`

const FormWrapper = styled.div`
  margin: 2.5rem 4rem 0;
  ${media.lessThan('small')`
    margin: 1.5rem 2rem 0;
  `}
`

const BackButton = styled(ActionButton)`
  margin-top: 1.3rem;
  width: 100%;
`

const BannerAdsContainer = styled.div`
  overflow: hidden;
  margin-bottom: 1rem;
`

const BannerAdContainer = styled.div`
  width: 100%;
  padding: 0 0.35rem;
`
const BannerAd = styled.img`
  object-fit: cover;
  height: 17rem;
  width: 25.5rem;
  border-radius: var(--border-radius-button);

  ${media.lessThan('small')`
  height: 55vw;
  width: 82.5vw;
  `}
`
// const dropOffEvent = () => {
// setGAEvent('sign_up', 'sign_up_drop_off', 'registration_card_linking')
// }

const ZingIconComponent = styled.img.attrs({ src: ZingIcon })`
  margin-bottom: 1rem;
`

const BannerCardContainer = styled.div`
  margin: 0 0.35rem;
  height: 17rem;
  width: 25.5rem;
  border-radius: var(--border-radius-button);
  background-color: rgba(141, 122, 157, 0.4);
  display: grid;
  place-content: center;

  ${media.lessThan('small')`
  height: 55vw;
  width: 82.5vw;
  `}
`

const Text = styled.p`
  margin: 0;
  color: #ffffff;
  font-weight: 500;
  text-align: center;
`

const LoadingBannerAdCard = ({ error, onRetry }) => (
  <BannerCardContainer onClick={error ? onRetry : e => e.preventDefault()}>
    <ZingIconComponent />
    {error && (
      <>
        <Text>Failed to load Banner Ads.</Text>
        <Text>Tap to retry.</Text>
      </>
    )}
  </BannerCardContainer>
)

const cardErrorDefaultType = {
  errorType: null,
  display: false,
}
function MainView({
  step,
  primaryInfo,
  setStep,
  accessToken,
  saveAccessToken,
  redirectAfterAuth,
  push,
  doHandleOnLoad,
  doDeleteCard,
  doContinueAddCard,
  doGetApiKey,
  browser,
  setDevice,
  errorList,
  setError,
  doGetAllAds,
  doLoadCardList,
  location = {},
}) {
  const [showAddPage, setShowAddPage] = React.useState(false)
  const [cardList, setCardList] = React.useState([])
  const [cardError, setCardsError] = React.useState(cardErrorDefaultType)
  const [apiKey, setApiKey] = React.useState('')
  const [programCode, setProgramCode] = React.useState('')
  const [rerender, setRerender] = React.useState(0)
  const [showSuccessModal, setShowSuccessModal] = React.useState(false)
  const [cardAdded, setCardAdded] = React.useState({})
  const [addCardError, setAddCardError] = React.useState('')
  const [bannerAds, setBannerAds] = React.useState([])
  const bannerAdsSlider = React.createRef()

  React.useEffect(() => {
    getBannerAds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (location)
      setGACustomPageView({
        page: location.pathname,
        title: 'Registration - Card Linking',
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  React.useEffect(() => {
    if (window.document.cookie.includes('web_uuid=')) {
      return
    }
    const { name, os, version } = browser
    postDevices({
      platform: 'web',
      deviceName: name,
      deviceModel: os,
      osVersion: version,
    }).then(res => {
      setDevice(res.data)
    })
  }, [browser, setDevice])

  const displayAddPage = () => {
    doGetApiKey({
      accessToken,
      setApiKey,
      setProgramCode,
    })()
    setGACustomEvent('button_cta', {
      screen_name: 'Registration - Card Linking',
      button_name: 'Add Credit Card',
    })
    setShowAddPage(true)
  }

  const getBannerAds = () => {
    doGetAllAds &&
      doGetAllAds({
        setBannerAds,
        onEmpty: onEmptyBannerAds,
        onError: onErrorBannerAds,
      })
  }

  const onErrorBannerAds = () => {
    setError({ name: BANNER_ADS })
  }

  const onEmptyBannerAds = () => {
    setError({ name: BANNER_ADS_EMPTY })
  }

  const redirectToThirdParty = async (redirectUri, code) => {
    if (typeof window !== undefined && window.location && redirectUri) {
      window.location.href = redirectUri + (code ? `?code=${code}` : '')
    }
  }

  if (step !== ADD_CARD) {
    return <Redirect to={`/register${location.search || ''}`} />
  }

  if (primaryInfo === null) {
    return <Redirect to="/" />
  }

  const { firstName } = primaryInfo || {}

  return (
    <Layout variant="has-carousel">
      <Title>Add Card</Title>
      {errorList && !errorList.find(error => error.name === BANNER_ADS_EMPTY) && (
        <BannerAdsContainer>
          <CardSlider
            dataSource={bannerAds}
            loadingComponent={() => (
              <LoadingBannerAdCard
                key={JSON.stringify(errorList)}
                loading
                error={errorList.find(error => error.name === BANNER_ADS)}
                onRetry={getBannerAds}
              />
            )}
            renderItem={item => {
              const image = item.images.find(img => img.type === 'BANNER')
              return (
                <BannerAdContainer>
                  <BannerAd
                    key={item.id}
                    alt={item.title}
                    src={image.imageUrl}
                  />
                </BannerAdContainer>
              )
            }}
            ref={bannerAdsSlider}
            bannerAds
          />
        </BannerAdsContainer>
      )}
      <FormWrapper>
        {showAddPage && (
          <>
            <AddCardForm
              key={rerender}
              addCardError={addCardError}
              apiKey={apiKey}
              programCode={programCode}
              onLoad={doHandleOnLoad({
                accessToken,
                rerender,
                setCardAdded,
                setShowAddPage,
                setAddCardError,
                setCardList,
                setShowSuccessModal,
                setRerender,
                setCardsError,
                cardErrorDefaultType,
              })}
              onConsentClick={() =>
                setGACustomEvent('screen_view', {
                  screen_name: 'Registration - Card Linking - Consent',
                })
              }
            />
            <BackButton onClick={() => setShowAddPage(false)}>Back</BackButton>
          </>
        )}
        {!showAddPage && (
          <>
            <CardList
              items={cardList}
              loadingStatus={1}
              onDelete={doDeleteCard({
                accessToken,
                cardList,
                setCardList,
              })}
              onAddClick={displayAddPage}
              firstName={truncateString(firstName, 20)}
              cardError={{
                ...cardError,
                onRetry: doLoadCardList({
                  accessToken,
                  setCardList,
                  setCardsError,
                  cardErrorDefaultType,
                }),
              }}
              hasCardError={cardError.display}
              hasCardsErrorButton
              registration
            />
            <AccessibleForm
              key={cardList.length}
              initialValues={{ cardCount: cardList.length }}
              onSubmit={doContinueAddCard({
                accessToken,
                redirectAfterAuth,
                saveAccessToken,
                setStep,
                push,
                sendOnboarding: Boolean(cardList.length),
                primaryInfo,
                redirectToThirdParty,
                searchString: location.search,
              })}
              component={SkipAddCardForm}
              action="/a/cards/add"
            />
          </>
        )}
      </FormWrapper>
      <AddCardSuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        card={cardAdded}
      />
    </Layout>
  )
}

MainView.defaultProps = {
  primaryInfo: null,
  step: LANDING,
  redirectAfterAuth: '',
}

MainView.propTypes = {
  step: PropTypes.number,
  primaryInfo: PropTypes.shape(),
  accessToken: PropTypes.string.isRequired,
  redirectAfterAuth: PropTypes.string,
  setStep: PropTypes.func.isRequired,
  saveAccessToken: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  doHandleOnLoad: PropTypes.func.isRequired,
  doDeleteCard: PropTypes.func.isRequired,
  doContinueAddCard: PropTypes.func.isRequired,
  doGetApiKey: PropTypes.func.isRequired,
}

export default MainView
